'use client';

import { useState } from 'react';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  anonymizeText,
  cleanClipboardText,
  createPrivacyJson,
  redactEmails,
  redactPhoneNumbers,
  scanJsonPii,
  type EmailRedactionMode,
  type PhoneRedactionMode,
} from '@/lib/browser-privacy-tools';

type Variant =
  | 'text-anonymizer'
  | 'email-redactor'
  | 'phone-number-redactor'
  | 'json-pii-scanner-lite'
  | 'clipboard-cleaner';

type ToolResult =
  | {
      kind: 'text';
      output: string;
      note?: string | null;
      jsonText: string;
    }
  | {
      kind: 'json-pii';
      findingsJson: string;
      redactedJson: string;
      findingCount: number;
    };

const sampleText = `Contact jane.doe@example.com or +1 (415) 555-0199.
Dashboard link: https://cleanor.app/account
Server IP: 192.168.10.24
Customer ID: 9876543210`;

const sampleJson = `{
  "email": "jane.doe@example.com",
  "phone": "+1 415 555 0199",
  "token": "sk_live_1234567890abcdefghijklmnop",
  "profile": {
    "backup_ip": "10.24.0.12",
    "support_url": "https://cleanor.app/support"
  }
}`;

const toolCopy: Record<
  Variant,
  {
    title: string;
    description: string;
    badge: string;
    actionLabel: string;
    successLabel: string;
    placeholder: string;
    downloadName: string;
  }
> = {
  'text-anonymizer': {
    title: 'Anonymize pasted text with practical placeholder redaction',
    description:
      'Paste text, replace emails, phones, URLs, IPs, and long identifiers with placeholders, and export the cleaned result locally.',
    badge: 'Privacy',
    actionLabel: 'Anonymize text',
    successLabel: 'Text anonymization',
    placeholder: sampleText,
    downloadName: 'anonymized.txt',
  },
  'email-redactor': {
    title: 'Redact email addresses from pasted text or lists',
    description:
      'Paste text or email lists, replace emails with a placeholder or mask the local part, and export the cleaned result locally.',
    badge: 'Email',
    actionLabel: 'Redact emails',
    successLabel: 'Email redaction',
    placeholder: sampleText,
    downloadName: 'emails-redacted.txt',
  },
  'phone-number-redactor': {
    title: 'Redact phone numbers from logs, chats, and support text',
    description:
      'Paste text, replace phone numbers with a placeholder or mask most digits, and export the safer result locally.',
    badge: 'Phone',
    actionLabel: 'Redact phone numbers',
    successLabel: 'Phone redaction',
    placeholder: sampleText,
    downloadName: 'phones-redacted.txt',
  },
  'json-pii-scanner-lite': {
    title: 'Scan pasted JSON for likely PII and secret-like values',
    description:
      'Paste JSON, inspect likely PII by path, and export both findings JSON and an optional redacted JSON output locally.',
    badge: 'JSON',
    actionLabel: 'Scan JSON',
    successLabel: 'JSON PII scan',
    placeholder: sampleJson,
    downloadName: 'json-pii-findings.json',
  },
  'clipboard-cleaner': {
    title: 'Clean pasted clipboard text and normalize hidden formatting junk',
    description:
      'Paste text or read it from the clipboard, clean invisible Unicode, smart punctuation, line endings, and broken whitespace, then copy the cleaned result.',
    badge: 'Clipboard',
    actionLabel: 'Clean clipboard text',
    successLabel: 'Clipboard cleanup',
    placeholder: `\uFEFF“Cleanor”\u00A0notes...\n\nLine  two   with   broken spacing`,
    downloadName: 'clipboard-cleaned.txt',
  },
};

function createTextDownload(text: string, fileName: string, mimeType = 'text/plain;charset=utf-8') {
  const url = URL.createObjectURL(new Blob([text], { type: mimeType }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  throw new Error('The browser could not access the clipboard.');
}

export default function BrowserPrivacyTool({ variant }: { variant: Variant }) {
  const copy = toolCopy[variant];
  const [inputValue, setInputValue] = useState('');
  const [emailMode, setEmailMode] = useState<EmailRedactionMode>('placeholder');
  const [phoneMode, setPhoneMode] = useState<PhoneRedactionMode>('placeholder');
  const [result, setResult] = useState<ToolResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isReadingClipboard, setIsReadingClipboard] = useState(false);

  function resetOutput() {
    setResult(null);
    setErrorMessage(null);
    setCreditMessage(null);
    setCopyMessage(null);
  }

  async function readClipboard() {
    if (!navigator.clipboard?.readText) {
      setErrorMessage('This browser cannot read the clipboard directly.');
      return;
    }

    setIsReadingClipboard(true);
    setErrorMessage(null);

    try {
      const next = await navigator.clipboard.readText();
      setInputValue(next);
      resetOutput();
    } catch {
      setErrorMessage('Clipboard access was denied or is not available in this browser.');
    } finally {
      setIsReadingClipboard(false);
    }
  }

  async function runTool() {
    if (!inputValue.trim()) {
      setErrorMessage('Paste some text or JSON first.');
      return;
    }

    setIsRunning(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setCopyMessage(null);

    try {
      const credits = await consumeToolUsage(
        variant,
        'Credits are not available for another privacy-tool run right now.',
      );

      switch (variant) {
        case 'text-anonymizer': {
          const output = anonymizeText(inputValue);
          setResult({
            kind: 'text',
            output,
            note: 'Replaces emails, phones, URLs, IPs, and long IDs with placeholders.',
            jsonText: createPrivacyJson({ output }),
          });
          break;
        }
        case 'email-redactor': {
          const output = redactEmails(inputValue, emailMode);
          setResult({
            kind: 'text',
            output,
            note:
              emailMode === 'mask-local'
                ? 'Mode: keep domain, mask local part.'
                : 'Mode: replace with [email].',
            jsonText: createPrivacyJson({ mode: emailMode, output }),
          });
          break;
        }
        case 'phone-number-redactor': {
          const output = redactPhoneNumbers(inputValue, phoneMode);
          setResult({
            kind: 'text',
            output,
            note:
              phoneMode === 'placeholder'
                ? 'Mode: replace with [phone].'
                : phoneMode === 'mask-last-2'
                  ? 'Mode: keep last 2 digits.'
                  : 'Mode: keep last 4 digits.',
            jsonText: createPrivacyJson({ mode: phoneMode, output }),
          });
          break;
        }
        case 'json-pii-scanner-lite': {
          const scanned = scanJsonPii(inputValue);
          setResult({
            kind: 'json-pii',
            findingsJson: scanned.findingsJson,
            redactedJson: scanned.redactedJson,
            findingCount: scanned.findings.length,
          });
          break;
        }
        case 'clipboard-cleaner': {
          const output = cleanClipboardText(inputValue);
          setResult({
            kind: 'text',
            output,
            note: 'Normalizes Unicode, smart punctuation, line endings, and broken whitespace.',
            jsonText: createPrivacyJson({ output }),
          });
          break;
        }
      }

      setCreditMessage(formatToolCreditSuccess(copy.successLabel, credits));
    } catch (error) {
      setResult(null);
      setErrorMessage(
        error instanceof Error ? error.message : 'This privacy task could not be completed.',
      );
    } finally {
      setIsRunning(false);
    }
  }

  async function handleCopy(text: string, label: string) {
    try {
      await copyText(text);
      setCopyMessage(label);
      setErrorMessage(null);
    } catch {
      setErrorMessage('The browser could not copy the current output.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{copy.badge}</span>
        <h2 className="tool-live-title">{copy.title}</h2>
        <p className="tool-live-subtitle">{copy.description}</p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            {variant === 'email-redactor' ? (
              <label className="tool-live-field">
                <span>Email redaction mode</span>
                <select
                  onChange={(event) => {
                    setEmailMode(event.target.value as EmailRedactionMode);
                    resetOutput();
                  }}
                  value={emailMode}
                >
                  <option value="placeholder">Replace with [email]</option>
                  <option value="mask-local">Mask local part, keep domain</option>
                </select>
              </label>
            ) : null}

            {variant === 'phone-number-redactor' ? (
              <label className="tool-live-field">
                <span>Phone redaction mode</span>
                <select
                  onChange={(event) => {
                    setPhoneMode(event.target.value as PhoneRedactionMode);
                    resetOutput();
                  }}
                  value={phoneMode}
                >
                  <option value="placeholder">Replace with [phone]</option>
                  <option value="mask-last-4">Mask all but last 4 digits</option>
                  <option value="mask-last-2">Mask all but last 2 digits</option>
                </select>
              </label>
            ) : null}

            <label className="tool-live-field tool-live-field-full">
              <span>{variant === 'json-pii-scanner-lite' ? 'JSON input' : 'Text input'}</span>
              <textarea
                onChange={(event) => {
                  setInputValue(event.target.value);
                  resetOutput();
                }}
                placeholder={copy.placeholder}
                spellCheck={variant !== 'json-pii-scanner-lite'}
                value={inputValue}
              />
            </label>
          </div>

          <div className="tool-live-actions">
            {variant === 'clipboard-cleaner' ? (
              <button
                className="button button-secondary"
                disabled={isReadingClipboard}
                onClick={() => void readClipboard()}
                type="button"
              >
                {isReadingClipboard ? 'Reading…' : 'Read clipboard'}
              </button>
            ) : null}
            <button
              className="button"
              disabled={isRunning}
              onClick={() => void runTool()}
              type="button"
            >
              {isRunning ? 'Working…' : copy.actionLabel}
            </button>
          </div>

          {errorMessage ? <p className="status-message status-error">{errorMessage}</p> : null}
          {creditMessage ? <p className="status-message status-success">{creditMessage}</p> : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <aside className="tool-live-panel">
          {result?.kind === 'text' ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.output, 'Cleaned text copied.')}
                  type="button"
                >
                  Copy output
                </button>
                <button
                  className="button button-secondary"
                  onClick={() => createTextDownload(result.output, copy.downloadName)}
                  type="button"
                >
                  Download TXT
                </button>
                <button
                  className="button button-secondary"
                  onClick={() =>
                    createTextDownload(
                      result.jsonText,
                      `${copy.downloadName.replace(/\.txt$/i, '')}.json`,
                      'application/json;charset=utf-8',
                    )
                  }
                  type="button"
                >
                  Download JSON
                </button>
              </div>
              {result.note ? (
                <p className="tool-live-panel-note">
                  <strong>Note</strong>
                  <span>{result.note}</span>
                </p>
              ) : null}
              <div className="tool-live-preview">
                <h3>Output</h3>
                <pre className="tool-markdown-code">{result.output}</pre>
              </div>
            </>
          ) : null}

          {result?.kind === 'json-pii' ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.findingsJson, 'Findings JSON copied.')}
                  type="button"
                >
                  Copy findings
                </button>
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy(result.redactedJson, 'Redacted JSON copied.')}
                  type="button"
                >
                  Copy redacted JSON
                </button>
              </div>
              <div className="tool-live-stats">
                <div>
                  <strong>Findings</strong>
                  <span>{result.findingCount}</span>
                </div>
                <div>
                  <strong>Mode</strong>
                  <span>Heuristic scan</span>
                </div>
              </div>
              <div className="tool-live-preview">
                <h3>Findings JSON</h3>
                <pre className="tool-markdown-code">{result.findingsJson}</pre>
              </div>
              <div className="tool-live-preview">
                <h3>Redacted JSON</h3>
                <pre className="tool-markdown-code">{result.redactedJson}</pre>
              </div>
            </>
          ) : (
            <div className="tool-live-empty">
              <p>Run the privacy tool to preview the cleaned or redacted output here.</p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
